import { useEffect, useState } from "react";
import type { BalancesResult, MemberBalance } from "../../shared/types";
import { api } from "../lib/api";
import { formatMoney, formatDayMonth } from "../lib/format";
import Modal from "./Modal";

// Read-only view of each member's balance + a simple statement (transactions
// since the balance was set, with a running total). Updating is a separate,
// deliberate action reached via the "Update balances" button.
export default function BalancesModal({
  onClose,
  onUpdate,
}: {
  onClose: () => void;
  onUpdate: () => void;
}) {
  const [data, setData] = useState<BalancesResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.balances().then(setData).catch((e) => setError((e as Error).message));
  }, []);

  return (
    <Modal title="Balances" onClose={onClose}>
      {error ? (
        <div className="state state--error" role="alert">{error}</div>
      ) : data == null ? (
        <div className="state">Loading…</div>
      ) : data.members.length === 0 ? (
        <div className="bal-empty">
          <p>No balances set yet.</p>
          <button className="btn btn--primary" onClick={onUpdate}>Set a balance</button>
        </div>
      ) : (
        <div className="bal">
          <div className="bal-combined">
            <div>
              <span className="bal-combined__label">Combined remaining</span>
              <strong className="bal-combined__value">{formatMoney(data.combinedRemainingMinor)}</strong>
            </div>
            <span className="bal-combined__sub">of {formatMoney(data.combinedBalanceMinor)} set</span>
          </div>

          {data.members.map((m) => <MemberRow key={m.memberId} m={m} />)}

          <p className="bal-hint">Tap a person to see the transactions behind their balance.</p>

          <div className="form__actions">
            <button className="btn btn--ghost" onClick={onClose}>Close</button>
            <button className="btn btn--primary" onClick={onUpdate}>Update balances</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function MemberRow({ m }: { m: MemberBalance }) {
  const [open, setOpen] = useState(false);
  const spent = m.balanceMinor - m.remainingMinor; // net change since the snapshot

  return (
    <div className="bal-card">
      <button className="bal-card__head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="bal-card__who" style={{ background: m.colour }}>{m.initial}</span>
        <span className="bal-card__main">
          <span className="bal-card__name">{m.name}</span>
          <span className="bal-card__sub">Balance {formatMoney(m.balanceMinor)} · set {formatDayMonth(m.asOf)}</span>
        </span>
        <span className="bal-card__figs">
          <strong className="bal-card__remain">{formatMoney(m.remainingMinor)}</strong>
          <span className="bal-card__remain-label">remaining</span>
        </span>
        <svg className={"bal-card__chev" + (open ? " is-open" : "")} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
      </button>

      {open && (
        <div className="bal-txns">
          {m.transactions.length === 0 ? (
            <p className="bal-txns__empty">No transactions since you set this balance.</p>
          ) : (
            <>
              <div className="bal-txn bal-txn--head">
                <span>Date</span>
                <span>What</span>
                <span className="bal-txn__amt">Amount</span>
                <span className="bal-txn__run">Balance</span>
              </div>
              <ul className="bal-txn-list">
                {m.transactions.map((t, i) => (
                  <li className="bal-txn" key={i}>
                    <span className="bal-txn__date">{formatDayMonth(t.date)}</span>
                    <span className="bal-txn__what">
                      <span className={"bal-txn__type bal-txn__type--" + t.type.toLowerCase().replace(/\s+/g, "")}>{t.type}</span>
                      <span className="bal-txn__label">{t.label}</span>
                    </span>
                    <span className={"bal-txn__amt " + (t.direction === "in" ? "bal-txn__amt--in" : "bal-txn__amt--out")}>
                      {t.direction === "in" ? "+" : "−"}{formatMoney(t.amountMinor)}
                    </span>
                    <span className="bal-txn__run">{formatMoney(t.runningMinor)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="bal-txns__start">
            <span>Balance set</span>
            <span>{formatMoney(m.balanceMinor)} on {formatDayMonth(m.asOf)}</span>
          </div>
          {spent !== 0 && (
            <div className="bal-txns__net">
              {spent > 0 ? `${formatMoney(spent)} out since` : `${formatMoney(-spent)} in since`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
